<template>
  <div class="mt-2">
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-alert dense outlined type="error" v-if="showError">
      {{ errorMsg }}
    </v-alert>

    <!-- We have to hardcode the height because otherwise the UI will shift when the buttons appear/dissapear -->
    <v-row class="my-1" style="height: 58px">
      <v-col cols="8">
        <v-tooltip top :disabled="!isCreatedByDeleted">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <v-btn
                v-if="
                  $store.state.bpInstModifyPermission && !locked && isActive
                "
                :disabled="!allowedActions?.canRetry || isCreatedByDeleted"
                class="mr-4"
                color="primary"
                @click="retry"
              >
                <v-icon left> pic-refresh </v-icon>
                {{ $t("global.action.retry") }}
              </v-btn>
            </span>
          </template>
          <span>{{ $t("global.msg.retry-deleted-instance") }}</span>
        </v-tooltip>

        <v-tooltip top :disabled="!isCreatedByDeleted">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <v-btn
                v-if="
                  $store.state.bpInstModifyPermission &&
                  !locked &&
                  taskType === 'runCreation' &&
                  isActive
                "
                :disabled="
                  !allowedActions?.canRetryAndStart || isCreatedByDeleted
                "
                class="mr-4"
                color="primary"
                @click="retryAndStart"
              >
                <v-icon left> pic-play-fill </v-icon>
                {{ $t("global.action.retry-and-run") }}
              </v-btn>
            </span>
          </template>
          <span>{{ $t("global.msg.retry-deleted-instance") }}</span>
        </v-tooltip>

        <v-btn
          v-if="$store.state.bpInstModifyPermission && !locked && isActive"
          color="error"
          :disabled="!allowedActions?.canCancel"
          @click="cancel"
        >
          <v-icon left> pic-close </v-icon>
          {{ $t("global.action.cancel") }}
        </v-btn>
      </v-col>

      <v-spacer></v-spacer>

      <v-col>
        <strong> {{ $t("global.business-process.task") + ": " }}</strong>
        <router-link
          class="ooliba-font-color-blue"
          :to="{
            name: 'Process task',
            params: { taskId: id },
          }"
          >{{ name + " (v." + (version + 1) + ")" }}</router-link
        >
      </v-col>
    </v-row>

    <div v-if="active">
      <task-run-list
        v-if="taskType === 'runCreation'"
        :flowTaskId="flowTaskId"
        :taskStatus="taskStatus"
      >
      </task-run-list>

      <file-upload-task
        v-if="taskType === 'fileUpload'"
        :fileEnv="fileEnv"
        :flowTaskId="flowTaskId"
        :taskStatus="taskStatus"
      >
      </file-upload-task>

      <evaluation-task
        v-if="taskType === 'evaluation'"
        :flowTaskId="flowTaskId"
        :taskStatus="taskStatus"
      >
      </evaluation-task>
    </div>
  </div>
</template>

<script>
import { get } from "@/model/api";
import { baseUrl } from "@/model/http-common";
import store from "@/store/index";
import TaskRunList from "@/components/businessProcess/TaskRunList";
import FileUploadTask from "@/components/businessProcess/FileUploadTask";
import EvaluationTask from "@/components/businessProcess/EvaluationTask";

export default {
  name: "TaskDetails",
  components: {
    TaskRunList,
    FileUploadTask,
    EvaluationTask,
  },

  props: {
    flowTaskId: Number,
    id: Number,
    name: String,
    version: Number,
    fileEnv: String,
    locked: String,
    isCreatedByDeleted: Boolean,
    isActive: Boolean,
  },

  data() {
    return {
      allowedActions: {},
      taskType: "",
      taskStatus: undefined,
      showError: undefined,
      errorMsg: undefined,

      active: false,
      loading: false,
      actionsTimerId: undefined,
      statusTimerId: undefined,
      eventSource: undefined,
      sseRetryCount: 0,
      sseReconnectTimer: undefined,
    };
  },

  computed: {},

  methods: {
    async getStatus() {
      return await get("/flow-task/" + this.flowTaskId + "/status").catch(
        (error) => this.onError(error)
      );
    },

    async getAllowedActions() {
      return await get(
        "/flow-task/" + this.flowTaskId + "/allowed-actions"
      ).catch((error) => this.onError(error));
    },

    async getTaskType() {
      return await get("/flow-task/" + this.flowTaskId + "/type").catch(
        (error) => this.onError(error)
      );
    },

    async getTrigger() {
      return await get("/flow-task/" + this.flowTaskId + "/trigger").catch(
        (error) => this.onError(error)
      );
    },

    async retry() {
      await get("/flow-task/" + this.flowTaskId + "/invalidate")
        .then(() => {
          this.showTaskWhenStatusEquals([
            "RUNNING",
            "WAITING_FOR_INPUT",
            "READY_TO_RUN",
          ]);
          this.allowedActions = this.getAllowedActions();
        })
        .catch((error) => this.onError(error));
    },

    async retryAndStart() {
      await get("/flow-task/" + this.flowTaskId + "/retry-and-run")
        .then(() => {
          this.showTaskWhenStatusEquals(["RUNNING", "WAITING_FOR_INPUT"]);
          this.allowedActions = this.getAllowedActions();
        })
        .catch((error) => this.onError(error));
    },

    async cancel() {
      await get("/flow-task/" + this.flowTaskId + "/cancel")
        .then(() => {
          this.showTaskWhenStatusEquals(["CANCELLED"]);
          this.allowedActions = this.getAllowedActions();
        })
        .catch((error) => this.onError(error));
    },

    onError(error) {
      this.$store.commit("showError", error);
    },

    connectAllowedActionsSse() {
      this.closeAllowedActionsSse();

      let url =
        baseUrl + "/flow-task/" + this.flowTaskId + "/allowed-actions/stream";
      const token = store.state.keycloak?.token;
      if (token) {
        url += "?token=" + encodeURIComponent(token);
      }
      const es = new EventSource(url);
      this.eventSource = es;

      es.addEventListener("allowed-actions-update", (event) => {
        this.sseRetryCount = 0;
        this.allowedActions = JSON.parse(event.data);
      });

      es.onerror = () => {
        this.closeAllowedActionsSse();
        this.sseRetryCount++;
        if (this.sseRetryCount <= 3) {
          const delay = Math.pow(2, this.sseRetryCount - 1) * 1000;
          this.sseReconnectTimer = window.setTimeout(() => {
            this.connectAllowedActionsSse();
          }, delay);
        } else {
          this.continuouslyUpdateAllowedActions();
        }
      };
    },

    closeAllowedActionsSse() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = undefined;
      }
      clearTimeout(this.sseReconnectTimer);
    },

    continuouslyUpdateAllowedActions() {
      this.actionsTimerId = window.setTimeout(() => {
        this.getAllowedActions().then((res) => {
          this.allowedActions = res;
        });
        this.continuouslyUpdateAllowedActions();
      }, 2200);
    },

    showTaskWhenStatusEquals(statuses) {
      this.loading = true;

      this.statusTimerId = window.setTimeout(() => {
        this.getStatus().then((status) => {
          if (status === "ERROR") {
            this.updateData();
            this.loading = false;
          } else {
            if (statuses.some((s) => s === status)) {
              this.updateData();
              this.loading = false;
            } else {
              this.showTaskWhenStatusEquals(statuses);
            }
          }
        });
      }, 300);
    },

    async getRuns() {
      return await get("/flow-task/" + this.flowTaskId + "/runs").catch(
        (error) => {
          this.onError(error);
        }
      );
    },

    async updateData() {
      this.taskType = "";
      this.allowedActions = {};
      this.taskStatus = await this.getStatus();
      this.active = this.taskStatus !== "READY_TO_RUN";
      if (this.taskStatus === "ERROR") {
        const runs = await this.getRuns();
        let latestRunStatus;
        if (runs && runs[0]) {
          latestRunStatus = runs[0].status;
        }
        this.showError = !(
          latestRunStatus &&
          (latestRunStatus === "ERROR" || latestRunStatus === "INIT_ERROR")
        );
        this.errorMsg = await this.getTrigger();
      } else {
        this.showError = false;
      }
      if (this.active) {
        this.allowedActions = await this.getAllowedActions();
        this.taskType = await this.getTaskType();
      }
    },
  },

  watch: {
    flowTaskId: async function () {
      this.closeAllowedActionsSse();
      this.sseRetryCount = 0;
      await this.updateData();
      this.connectAllowedActionsSse();
    },
  },

  async created() {
    await this.updateData();
    this.connectAllowedActionsSse();
  },

  destroyed() {
    this.closeAllowedActionsSse();
    clearTimeout(this.actionsTimerId);
    clearTimeout(this.statusTimerId);
  },
};
</script>
